import {
  InternalRefetchQueryDescriptor,
  useApolloClient,
} from "@apollo/client";
import { assertNever } from "@msys/common";
import {
  CardContainer,
  CollapseSection,
  LabeledValue,
  LabeledValueWithButton,
  MenuButton,
  ModalOpenButton,
  Select,
} from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Edit as EditIcon } from "@mui/icons-material";
import { Functions as FunctionsIcon } from "@mui/icons-material";
import {
  Button,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, useFormik } from "formik";
import { differenceWith, keyBy } from "lodash-es";
import React from "react";
import {
  DocType,
  ItemTemplateSearchFilterPropertyFilterInput,
  TemplateSearchFilterPropertyFilterInput,
  EntitySearchArrayOfFilterOperator,
  EntitySearchFilterOperator,
  EntitySearchBoolFilterOperator,
  EntitySearchBoolInFilterOperator,
  EntitySearchNumberBetweenFilterOperator,
  EntitySearchNumberFilterOperator,
  EntitySearchNumberInFilterOperator,
  EntitySearchTextFilterOperator,
  EntitySearchTextInFilterOperator,
  PermissionName,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import type { DocumentByType } from "../../../auth/RestrictedByDocumentPermissionByType.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import {
  ArrayFilterOperatorOptions,
  BoolFilterOperatorOptions,
  NumberFilterOperatorOptions,
  TextFilterOperatorOptions,
} from "../../products/ProductSearchFilterOperatorOptions.js";
import { ItemTemplateSearchFilterPropertyFilterFragment } from "../../templates/templateSearchFilters.generated.js";
import { TemplateTypeAtVersionWarningButton } from "../../templateTypes/TemplateTypeAtVersionWarningButton.js";
import { TemplateTypeInput } from "../../templateTypes/TemplateTypeInput.js";
import { TemplateTypesSearchModal } from "../../templateTypes/TemplateTypesSearchModal.js";
import { TemplateTypesSearchModal_TemplateTypeFragment } from "../../templateTypes/TemplateTypesSearchModal.generated.js";
import type { PropertyOption } from "../modals/ExpressionInputWithVariables.js";
import {
  EXPRESSIONS_UPDATE_PERMISSION_BY_DOC_TYPE,
  useCanWriteExpressions,
} from "../expressionWriteAccess.js";
import type { Props2NonComputedAllFragment } from "../properties.generated.js";
import {
  getEnhancedPropertyLabel,
  getEnhancedTemplateSearchFilterPropLabel,
} from "../properties.js";
import {
  QuestionControlSection,
  QuestionControlSectionFormValues,
} from "../QuestionControlSection.js";
import { templateSearchFilterPropertyFiltersToRestoreAfterTypeChange } from "../templateSearchFilterExpressionUtils.js";
import { useTemplateSearchFiltersMutations } from "../useTemplateSearchFiltersMutations.js";
import { SearchFilterField } from "../SearchFilterField.js";
import {
  SearchFilterOperatorsModal,
  type SearchFilterOperatorRow,
} from "../modals/SearchFilterOperatorsModal.js";
import {
  isComputedSearchFilterSlot,
  newAuthoredFilterForProp,
  searchFilterEntry,
  searchFilterFieldPropsForSlot,
  searchFilterOperator,
  searchFilterOperatorOptions,
  searchFilterValueText,
  searchFilterSlotToInput,
  searchFilterSlotWithExpression,
  searchFilterSlotWithOperator,
  searchFilterSlotWithoutExpression,
  searchFilterValueOptionsFromProps2,
} from "../searchFilterSlots.js";
import { SearchFilterValueInput } from "../SearchFilterValueInput.js";
import { useTemplateSearchSortingsMutations } from "../useTemplateSearchSortingsMutations.js";
import {
  TemplateSearchFilterDefinitionsFragment,
  TemplateTypePlaceholderBox_ItemFragment,
  TemplateTypePlaceholderBox_QuoteFragment,
  TemplateTypePlaceholderBox_QuoteTemplateFragment,
} from "./TemplateTypePlaceholderBox.generated.js";
import {
  useSetItemPlaceholderForTemplateTypeMutationWithWizardRefetch,
  useTemplateTypePlacerholderQuestionControlForm_ModifyWizardSettingsMutationWithWizardRefetch,
} from "./TemplateTypePlaceholderBox.mutations.js";

interface Props {
  projectId: string | null;
  docType: DocType;
  doc:
    | TemplateTypePlaceholderBox_QuoteTemplateFragment
    | TemplateTypePlaceholderBox_QuoteFragment;
  item: TemplateTypePlaceholderBox_ItemFragment;
  onUpdateDataRefetchQueries?: InternalRefetchQueryDescriptor[];
  isEditable?: boolean;
  documentViewerPermissions: PermissionName[];
  refetchWizardQuestions: () => Promise<unknown>;
}

export const TemplateTypePlaceholderBox = ({
  projectId,
  doc,
  item,
  onUpdateDataRefetchQueries,
  isEditable = false,
  docType,
  documentViewerPermissions,
  refetchWizardQuestions,
}: Props) => {
  const { t } = useTranslate([
    "QuoteItem",
    "ItemPropertyField",
    "Global",
    "TemplateTypes",
  ]);

  const document: DocumentByType = {
    docType,
    viewerPermissions: documentViewerPermissions,
  };

  const renderExpressionGate = (
    gated: React.ReactNode,
    fallback: React.ReactNode
  ) => (
    <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
      capability="expressions"
      permissionByType={EXPRESSIONS_UPDATE_PERMISSION_BY_DOC_TYPE}
      document={document}
      otherwise={fallback}
    >
      {gated}
    </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
  );

  const canWriteExpressions = useCanWriteExpressions(document);

  const client = useApolloClient();

  const [setPlaceholderForTemplateType, { loading: setTypeLoading }] =
    useSetItemPlaceholderForTemplateTypeMutationWithWizardRefetch({
      client,
      refetchWizardQuestions,
    });

  const { propertyFilters } = item.templateSearchFilterDefinitions;
  const templateTypePropsByKey = keyBy(
    item.placeholderForTemplateType?.templateType?.props2 ?? [],
    p => p.key
  );

  const searchSortingDefinitions = item.templateSearchSortingDefinitions;

  const { writeSlots, setFilterSlot, deleteFilterSlot } =
    useTemplateSearchFiltersMutations({
      projectId: projectId,
      docId: doc.id,
      itemId: item.id,
      propertyFilters,
      refetchQueries: onUpdateDataRefetchQueries,
    });

  const slotsToRestoreAfterTypeChange = (
    templateTypeProps: Props2NonComputedAllFragment[]
  ) =>
    templateSearchFilterPropertyFiltersToRestoreAfterTypeChange({
      filters: propertyFilters,
      templateTypeProps,
      canWriteExpressions,
    }).map(searchFilterSlotToInput);

  const {
    handleDeleteTemplateSearchPropertySortingDefinition,
    handleSetTemplateSearchPropertySortingDefinition,
  } = useTemplateSearchSortingsMutations({
    projectId: projectId,
    docId: doc.id,
    itemId: item.id,
    searchSortingDefinitions: searchSortingDefinitions,
    refetchQueries: onUpdateDataRefetchQueries,
  });

  // A filter selects the template that fills the placeholder's own interface
  // properties, so reading one of them here closes the cycle
  // property -> selection -> filter, which the dependency graph rejects on
  // save. Leave them out of the picker instead of offering a choice that
  // cannot be saved.
  const filterOutOwnProperties = React.useCallback(
    (option: PropertyOption) => option.itemId !== item.id,
    [item.id]
  );

  // One row per filter key now, so the picker diffs against the whole array —
  // it used to diff against the expression half only and would re-offer a
  // property that already had an authored filter.
  const unusedFilterProperties = differenceWith(
    item.placeholderForTemplateType?.templateType?.props2 ?? [],
    propertyFilters,
    (p, f) => p.key === f.key
  );

  const unusedSortingProperties = differenceWith(
    item.placeholderForTemplateType?.templateType?.props2 ?? [],
    searchSortingDefinitions.filter(
      d => d.__typename === "EntitySearchSortingPropertyValueSorting"
    ),
    (p, f) => p.key === f.key
  ).flatMap(p => (p.__typename === "Props2Number" ? p : []));

  const handleAddTemplateType = async (
    templateType: TemplateTypesSearchModal_TemplateTypeFragment
  ) => {
    await setPlaceholderForTemplateType({
      variables: {
        input: {
          itemId: item.id,
          docId: doc.id,
          projectId: projectId,
          templateTypeId: templateType.id,
        },
      },
    });

    // Switching type can leave filters referring to a property the new type no
    // longer has — these seed the picker, so a stale one means an unexplained
    // empty result. Gated on what there *was*, not on how much survived: the
    // mutation above clears the stored filters outright, so a switch where
    // every filter is compatible still needs the write.
    if (propertyFilters.length > 0) {
      await writeSlots(slotsToRestoreAfterTypeChange(templateType.props2));
    }
  };
  const filterRows: SearchFilterOperatorRow[] = propertyFilters.map(
    propertyFilter => ({
      key: propertyFilter.key,
      label: getEnhancedTemplateSearchFilterPropLabel(propertyFilter),
      value: searchFilterValueText(propertyFilter, {
        boolLabels: {
          yes: t("Yes", { ns: "Global" }),
          no: t("No", { ns: "Global" }),
        },
      }),
      isComputed: isComputedSearchFilterSlot(propertyFilter),
      operator: searchFilterOperator(propertyFilter),
      operatorOptions: searchFilterOperatorOptions(propertyFilter),
      onChangeOperator: (operator: EntitySearchFilterOperator) =>
        setFilterSlot(
          propertyFilter.key,
          searchFilterSlotWithOperator(propertyFilter, operator)
        ),
      onDelete: () => deleteFilterSlot(propertyFilter.key),
    })
  );

  if (
    item.isRootItem ||
    !(item.type === "section" || item.placeholderForTemplateType)
  )
    return null;

  return (
    <CardContainer
      title={t("Template type placeholder", { ns: "QuoteItem" })}
      isExpandable
      ActionButton={
        isEditable ? (
          !item.placeholderForTemplateType ? (
            <ModalOpenButton
              Modal={TemplateTypesSearchModal}
              modalProps={{
                handleSelectTemplateType: async (templateType, handleClose) => {
                  await handleAddTemplateType(templateType);
                  handleClose();
                },
              }}
            >
              <IconButton color="primary" size="small">
                <AddIcon />
              </IconButton>
            </ModalOpenButton>
          ) : (
            <>
              {item.placeholderForTemplateType.atRevision !==
              item.placeholderForTemplateType.templateType.revision ? (
                <TemplateTypeAtVersionWarningButton
                  templateTypeRevision={
                    item.placeholderForTemplateType.templateType.revision
                  }
                  atRevision={item.placeholderForTemplateType.atRevision}
                  onResolve={async () => {
                    if (item.placeholderForTemplateType) {
                      await setPlaceholderForTemplateType({
                        variables: {
                          input: {
                            projectId: projectId,
                            itemId: item.id,
                            docId: doc.id,
                            templateTypeId:
                              item.placeholderForTemplateType.templateType.id,
                          },
                        },
                      });

                      await writeSlots(
                        slotsToRestoreAfterTypeChange(
                          item.placeholderForTemplateType.templateType.props2
                        )
                      );
                    }
                  }}
                />
              ) : undefined}
            </>
          )
        ) : undefined
      }
    >
      {item.placeholderForTemplateType ? (
        <Stack
          direction="column"
          spacing={1}
          sx={{
            p: 1,
          }}
        >
          {/* No onClear: a placeholder's template type is its contract — it can
              be switched to another type, but not cleared away. */}
          <TemplateTypeInput
            templateTypeId={item.placeholderForTemplateType.templateType.id}
            canEdit={isEditable}
            onChange={async templateType => {
              await handleAddTemplateType(templateType);
            }}
          />
          <CollapseSection
            title={t("Search filters", { ns: "TemplateTypes" })}
            itemCount={propertyFilters.length}
            ActionButtons={
              isEditable ? (
                <Stack direction={"row"} spacing={1}>
                  {/* Disabled rather than unmounted while empty: the button owns
                      the open modal, which must survive deleting the last row. */}
                  <ModalOpenButton
                    Modal={SearchFilterOperatorsModal}
                    modalProps={{ rows: filterRows, renderExpressionGate }}
                  >
                    <IconButton
                      color={"primary"}
                      size={"small"}
                      disabled={propertyFilters.length === 0}
                      aria-label={t("Edit search filters", {
                        ns: "QuoteItem",
                      })}
                    >
                      <EditIcon />
                    </IconButton>
                  </ModalOpenButton>
                  <MenuButton
                    Icon={<AddIcon />}
                    buttonProps={{
                      disabled: unusedFilterProperties.length === 0,
                      "aria-label": t("Add search filter", {
                        ns: "QuoteItem",
                      }),
                    }}
                  >
                    {/* A filter is created without a value; the row then takes
                      either a value or a formula. */}
                    {unusedFilterProperties.map(prop => (
                      <MenuItem
                        key={prop.key}
                        onClick={async () => {
                          await setFilterSlot(
                            prop.key,
                            searchFilterSlotToInput(
                              newAuthoredFilterForProp(prop)
                            )
                          );
                        }}
                      >
                        {getEnhancedPropertyLabel(prop, false)}
                      </MenuItem>
                    ))}
                  </MenuButton>
                </Stack>
              ) : undefined
            }
          >
            <Grid
              container
              columns={2}
              rowSpacing={1}
              columnSpacing={2}
              sx={{
                padding: 1,
              }}
            >
              {propertyFilters.map(propertyFilter => (
                <Grid key={propertyFilter.key} size={1}>
                  <SearchFilterField
                    projectId={projectId}
                    docId={doc.id}
                    itemId={item.id}
                    label={getEnhancedTemplateSearchFilterPropLabel(
                      propertyFilter
                    )}
                    {...searchFilterFieldPropsForSlot(propertyFilter)}
                    valueInput={
                      <SearchFilterValueInput
                        slot={propertyFilter}
                        label={getEnhancedTemplateSearchFilterPropLabel(
                          propertyFilter
                        )}
                        valueOptions={searchFilterValueOptionsFromProps2(
                          templateTypePropsByKey[propertyFilter.key]
                        )}
                        disabled={!isEditable}
                        buildEntry={searchFilterEntry}
                        onChange={entry =>
                          setFilterSlot(propertyFilter.key, entry)
                        }
                      />
                    }
                    readOnly={!isEditable}
                    filterOptions={filterOutOwnProperties}
                    onSaveExpression={(operator, expr) =>
                      setFilterSlot(
                        propertyFilter.key,
                        searchFilterSlotWithExpression(
                          propertyFilter,
                          operator,
                          expr
                        )
                      )
                    }
                    onRemoveExpression={() =>
                      setFilterSlot(
                        propertyFilter.key,
                        searchFilterSlotWithoutExpression(propertyFilter)
                      )
                    }
                    renderExpressionGate={renderExpressionGate}
                  />
                </Grid>
              ))}
            </Grid>
          </CollapseSection>
          <CollapseSection
            title={t("Sorting", { ns: "TemplateTypes" })}
            itemCount={searchSortingDefinitions.length}
            ActionButtons={
              isEditable ? (
                <MenuButton
                  Icon={<AddIcon />}
                  buttonProps={{
                    disabled: unusedSortingProperties.length === 0,
                  }}
                >
                  {unusedSortingProperties.map(prop => {
                    switch (prop.__typename) {
                      case "Props2Number": {
                        return (
                          <MenuItem
                            key={prop.key}
                            onClick={async () => {
                              await handleSetTemplateSearchPropertySortingDefinition(
                                prop.key,
                                "number",
                                "asc"
                              );
                            }}
                          >
                            {getEnhancedPropertyLabel(prop, false)}
                          </MenuItem>
                        );
                      }
                      default:
                        assertNever(prop.__typename);
                    }
                  })}
                </MenuButton>
              ) : undefined
            }
          >
            <Grid
              container
              columns={2}
              rowSpacing={1}
              columnSpacing={2}
              sx={{
                padding: 1,
              }}
            >
              {searchSortingDefinitions.map((searchSorting, index) => {
                switch (searchSorting.__typename) {
                  case "EntitySearchSortingPropertyValueSorting": {
                    return (
                      <Grid key={searchSorting.key} size={1}>
                        <Stack
                          direction={"row"}
                          spacing={0.5}
                          sx={{
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Select
                            key={`${searchSorting.key}-input`}
                            label={searchSorting.key}
                            options={[
                              {
                                label: "asc",
                                value: "asc" as const,
                              },
                              {
                                label: "desc",
                                value: "desc" as const,
                              },
                            ]}
                            value={searchSorting.direction}
                            onChange={async value => {
                              await handleSetTemplateSearchPropertySortingDefinition(
                                searchSorting.key,
                                searchSorting.kind,
                                value
                              );
                            }}
                            disabled={!isEditable}
                          />
                          {isEditable && (
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={async () => {
                                await handleDeleteTemplateSearchPropertySortingDefinition(
                                  searchSorting.key
                                );
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Stack>
                      </Grid>
                    );
                  }
                  default:
                    assertNever(searchSorting.__typename);
                }
              })}
            </Grid>
          </CollapseSection>
          <TemplateTypeQuestionControlForm
            projectId={projectId}
            docId={item.docId}
            item={item}
            canEdit={isEditable}
            refetchWizardQuestions={refetchWizardQuestions}
          />
        </Stack>
      ) : isEditable ? (
        <Stack
          sx={{
            padding: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ModalOpenButton
            Modal={TemplateTypesSearchModal}
            modalProps={{
              handleSelectTemplateType: async (templateType, handleClose) => {
                await handleAddTemplateType(templateType);
                handleClose();
              },
            }}
          >
            <Button startIcon={<AddIcon />} color={"secondary"} size="small">
              {t("Add template type", { ns: "TemplateTypes" })}
            </Button>
          </ModalOpenButton>
        </Stack>
      ) : (
        <Stack
          sx={{
            padding: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "grey.600",
            }}
          >
            {t("No template type defined", { ns: "QuoteItem" })}
          </Typography>
        </Stack>
      )}
    </CardContainer>
  );
};

export function TemplateTypeQuestionControlForm({
  projectId,
  docId,
  item,
  canEdit = true,
  refetchWizardQuestions,
}: {
  projectId: string | null;
  docId: string;
  item: TemplateTypePlaceholderBox_ItemFragment;
  canEdit?: boolean;
  refetchWizardQuestions: () => Promise<unknown>;
}) {
  const client = useApolloClient();
  const [modifyWizardSettings] =
    useTemplateTypePlacerholderQuestionControlForm_ModifyWizardSettingsMutationWithWizardRefetch(
      {
        client,
        refetchWizardQuestions,
      }
    );

  return (
    <Formik<QuestionControlSectionFormValues>
      initialValues={{
        questionControl: item.wizardSettings.templateTypeQuestionControl,
        // askWhen:
        //   item.wizardSettings.templateTypeQuestionControl.at(0)?.askWhen ?? [],
        // askWhom:
        //   item.wizardSettings.templateTypeQuestionControl.at(0)?.askWhom ?? [],
        prompt: item.wizardSettings.promptTemplateType,
      }}
      onSubmit={async values => {
        await modifyWizardSettings({
          variables: {
            input: {
              projectId,
              docId,
              itemId: item.id,
              values: {
                templateTypeQuestionControl: values.questionControl,
                // templateTypeQuestionControl: [
                //   {
                //     askWhen: values.askWhen,
                //     askWhom: values.askWhom,
                //   },
                // ],
                promptTemplateType: values.prompt,
              },
            },
          },
        });
      }}
    >
      <Form>
        <QuestionControlSection
          readOnly={!canEdit}
          isInitiallyExpanded
          disabled={false}
        />
        <AutoSave />
      </Form>
    </Formik>
  );
}
